import React, { useState, useEffect, useCallback } from 'react'
import { Search, ChevronDown } from 'lucide-react'
import { AccountGroup, FunderFilter, AssociationLabel, ASSOCIATION_OPTIONS } from '../types'
import { fundersService } from '../services/fundersService'
import { ChangesTab } from './ChangesTab'
import { FunderOverviewTab } from './FunderOverviewTab'
import { AllCompanyAssociationsTab } from './AllCompanyAssociationsTab'

export const FundersDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'deals' | 'funders' | 'all-associations' | 'changes'>('deals')
  const [accountGroups, setAccountGroups] = useState<AccountGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FunderFilter>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [editingAssociation, setEditingAssociation] = useState<{ dealId: string; companyId: string } | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const accountGroupsData = await fundersService.getAccountGroups(filter)
      setAccountGroups(accountGroupsData)
    } catch (error) {
      console.error('Error loading funders data:', error)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter({ ...filter, search: searchTerm })
  }


  const handleAssociationChange = async (
    dealId: string,
    companyId: string,
    newAssociation: AssociationLabel
  ) => {
    try {
      await fundersService.changeAssociation(dealId, companyId, newAssociation)
      await loadData() // Reload data to reflect changes
      setEditingAssociation(null)
    } catch (error) {
      console.error('Error changing association:', error)
      alert('Failed to change association')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getAssociationColor = (label: string) => {
    switch (label.toLowerCase()) {
      case 'funder':
        return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }
      case 'supplier':
        return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }
      case 'primary':
        return { bg: '#fef3c7', text: '#d97706', border: '#fde68a' }
      default:
        return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' }
    }
  }

  if (loading && activeTab === 'deals') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '50%', 
            height: '32px', 
            width: '32px', 
            border: '2px solid #3b82f6', 
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
            Loading 2025 funders data...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: '#111827',
                fontFamily: 'Lexend Deca, sans-serif',
                marginBottom: '8px'
              }}>
                2025 Funders
              </h1>
              <p style={{ 
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Deals grouped by account and company associations
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => setActiveTab('deals')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'deals' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'deals' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'deals' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Deals & Associations
            </button>
            <button
              onClick={() => setActiveTab('funders')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'funders' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'funders' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'funders' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Funder Overview
            </button>
            <button
              onClick={() => setActiveTab('all-associations')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'all-associations' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'all-associations' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'all-associations' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              All Company Associations
            </button>
            <button
              onClick={() => setActiveTab('changes')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'changes' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'changes' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'changes' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Change History
            </button>
          </div>

        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'changes' ? (
        <ChangesTab onDataChanged={loadData} />
      ) : activeTab === 'funders' ? (
        <FunderOverviewTab filter={filter} />
      ) : activeTab === 'all-associations' ? (
        <AllCompanyAssociationsTab filter={filter} />
      ) : (
        <>
          {/* Search Bar */}
          <div style={{ 
            background: 'white', 
            borderBottom: '1px solid #e5e7eb',
            padding: '16px 24px'
          }}>
            <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search deals, companies, or accounts..."
                    style={{
                      width: '100%',
                      paddingLeft: '40px',
                      paddingRight: '16px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                  <Search style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '16px',
                    width: '16px',
                    color: '#9ca3af'
                  }} />
                </div>
                <button
                  type="submit"
                  style={{
                    padding: '12px 20px',
                    background: '#2563eb',
                    color: 'white',
                    fontWeight: '500',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          {/* Account Groups */}
          <div style={{ padding: '24px' }}>
            <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {accountGroups.map((accountGroup) => (
                  <div key={accountGroup.account} style={{ 
                    background: 'white', 
                    borderRadius: '12px', 
                    overflow: 'hidden', 
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb'
                  }}>
                    {/* Account Header */}
                    <div style={{
                      padding: '20px',
                      background: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2 style={{ 
                          fontSize: '20px', 
                          fontWeight: '600', 
                          color: '#111827',
                          fontFamily: 'Lexend Deca, sans-serif',
                          margin: 0
                        }}>
                          {accountGroup.account}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ 
                              fontSize: '18px', 
                              fontWeight: '600', 
                              color: '#10b981',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {formatCurrency(accountGroup.total_amount)}
                            </div>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#6b7280',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {accountGroup.deal_count} deals
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Deals List */}
                    <div style={{ padding: '0' }}>
                      {accountGroup.deals.map((deal, index) => (
                        <div key={deal.deal_id}>
                          {/* Deal Row */}
                          <div style={{
                            padding: '16px 20px',
                            borderBottom: deal.companies.length > 0 ? '1px solid #f3f4f6' : index < accountGroup.deals.length - 1 ? '1px solid #f3f4f6' : 'none'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                                <span style={{ 
                                  fontSize: '14px', 
                                  color: '#6b7280',
                                  fontFamily: 'Lexend Deca, sans-serif',
                                  minWidth: '80px'
                                }}>
                                  {formatDate(deal.closed_won_date)}
                                </span>
                                <span style={{ 
                                  fontSize: '16px', 
                                  fontWeight: '500', 
                                  color: '#111827',
                                  fontFamily: 'Lexend Deca, sans-serif',
                                  flex: 1
                                }}>
                                  {deal.deal_name}
                                </span>
                                <span style={{ 
                                  fontSize: '16px', 
                                  fontWeight: '600', 
                                  color: '#10b981',
                                  fontFamily: 'Lexend Deca, sans-serif',
                                  minWidth: '100px',
                                  textAlign: 'right'
                                }}>
                                  {formatCurrency(deal.deal_amount)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Companies Sub-row */}
                          {deal.companies.length > 0 && (
                            <div style={{
                              padding: '12px 20px 16px 20px',
                              background: '#f9fafb',
                              borderBottom: index < accountGroup.deals.length - 1 ? '1px solid #e5e7eb' : 'none'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '8px',
                                alignItems: 'flex-start'
                              }}>
                                {deal.companies.map((company, companyIndex) => {
                                  const colors = getAssociationColor(company.association_label)
                                  const isEditing = editingAssociation?.dealId === deal.deal_id && 
                                                   editingAssociation?.companyId === company.company_id
                                  
                                  return (
                                    <div key={`${company.company_id}-${companyIndex}`} style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '6px 12px',
                                      background: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #e5e7eb',
                                      fontSize: '14px',
                                      fontFamily: 'Lexend Deca, sans-serif'
                                    }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ 
                                          color: '#111827',
                                          fontWeight: '500'
                                        }}>
                                          {company.company_name}
                                        </span>
                                        {company.company_domain && (
                                          <span style={{ 
                                            color: '#6b7280',
                                            fontSize: '12px',
                                            fontWeight: '400'
                                          }}>
                                            {company.company_domain}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {isEditing ? (
                                        <div style={{ position: 'relative' }}>
                                          <select
                                            value={company.association_label}
                                            onChange={(e) => handleAssociationChange(
                                              deal.deal_id, 
                                              company.company_id, 
                                              e.target.value as AssociationLabel
                                            )}
                                            onBlur={() => setEditingAssociation(null)}
                                            autoFocus
                                            style={{
                                              padding: '2px 20px 2px 8px',
                                              borderRadius: '12px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              background: colors.bg,
                                              color: colors.text,
                                              border: `1px solid ${colors.border}`,
                                              appearance: 'none',
                                              cursor: 'pointer',
                                              fontFamily: 'Lexend Deca, sans-serif'
                                            }}
                                          >
                                            {ASSOCIATION_OPTIONS.map(option => (
                                              <option key={option} value={option}>
                                                {option}
                                              </option>
                                            ))}
                                          </select>
                                          <ChevronDown style={{
                                            position: 'absolute',
                                            right: '4px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            height: '12px',
                                            width: '12px',
                                            color: colors.text,
                                            pointerEvents: 'none'
                                          }} />
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => setEditingAssociation({ 
                                            dealId: deal.deal_id, 
                                            companyId: company.company_id 
                                          })}
                                          style={{
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            background: colors.bg,
                                            color: colors.text,
                                            border: `1px solid ${colors.border}`,
                                            cursor: 'pointer',
                                            fontFamily: 'Lexend Deca, sans-serif',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '0.8'
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '1'
                                          }}
                                        >
                                          {company.association_label}
                                        </button>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}